import { useEffect, useState, useCallback } from "react";
import { Plus, HardHat, Truck, Pencil, Power } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { SKILLS } from "@/lib/labels";
import { toast } from "sonner";

export default function AdminProfessionals() {
  const [pros, setPros] = useState(null);
  const [zones, setZones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    const [p, z] = await Promise.all([api.get("/admin/professionals"), api.get("/zones")]);
    setPros(p.data); setZones(z.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!pros) return <Loader />;

  const toggleActive = async (p) => {
    try { await api.put(`/admin/professionals/${p.user_id}`, { active: !p.active }); toast.success(p.active ? "Compte désactivé" : "Compte activé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle action={<button onClick={() => setShowForm(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-pro-btn"><Plus className="w-4 h-4" /> Créer un compte</button>}>Professionnels & Livreurs</PageTitle>

      {created && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4" data-testid="pro-created-banner">
          <p className="font-semibold text-green-800">Compte créé ✓ — transmettez ces identifiants :</p>
          <p className="text-sm text-green-700 mt-1 font-num">Email : {created.email} · Code d'activation : {created.activation_code}</p>
          <p className="text-xs text-green-600">Lien PWA : {window.location.origin}/login</p>
          <button onClick={() => setCreated(null)} className="text-xs text-green-700 underline mt-1">Fermer</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {pros.map((p) => (
          <Panel key={p.user_id} className="p-4 flex flex-col" data-testid={`pro-card-${p.user_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${p.role === "livreur" ? "bg-blue-100" : "bg-[#FDF3EE]"}`}>
                {p.role === "livreur" ? <Truck className="w-5 h-5 text-blue-600" /> : <HardHat className="w-5 h-5 text-[#C85A32]" />}
              </div>
              <div><p className="font-semibold">{p.name}</p><p className="text-xs text-slate-400">{p.role === "livreur" ? "Livreur" : "Professionnel"}</p></div>
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${p.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`} data-testid={`pro-status-${p.user_id}`}>{p.active ? "Actif" : "Inactif"}</span>
            </div>
            <p className="text-xs text-slate-500">{p.email} · {p.phone}</p>
            {p.skills?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{p.skills.map((s) => <span key={s} className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">{s}</span>)}</div>}
            {p.zones?.length > 0 && <p className="text-xs text-slate-400 mt-1">Zones : {p.zones.join(", ")}</p>}
            <p className="text-xs text-slate-400 mt-1">{p.missions_count} mission(s)</p>
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
              <button onClick={() => setEditing(p)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg py-1.5 hover:border-[#C85A32]/50" data-testid={`pro-edit-${p.user_id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
              <button onClick={() => toggleActive(p)} className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold rounded-lg py-1.5 ${p.active ? "text-red-600 border border-red-200 hover:bg-red-50" : "text-green-700 border border-green-200 hover:bg-green-50"}`} data-testid={`pro-toggle-${p.user_id}`}><Power className="w-3.5 h-3.5" /> {p.active ? "Désactiver" : "Activer"}</button>
            </div>
          </Panel>
        ))}
      </div>

      {showForm && <ProForm zones={zones} onClose={() => setShowForm(false)} onDone={(c) => { setCreated(c); load(); }} />}
      {editing && <EditProForm pro={editing} zones={zones} onClose={() => setEditing(null)} onDone={load} />}
    </div>
  );
}

function ProForm({ zones, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", role: "professional", skills: [], zones: [] });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, val) => setF((s) => ({ ...s, [k]: s[k].includes(val) ? s[k].filter((x) => x !== val) : [...s[k], val] }));

  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/professionals", f); toast.success("Compte créé"); onDone(data); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title="Créer un compte pro / livreur" onClose={onClose} testid="pro-form">
      <form onSubmit={submit} className="space-y-3">
        <SelectField label="Type de compte" value={f.role} onChange={set("role")} testid="prof-role" placeholder={null}
          options={[{ value: "professional", label: "Professionnel / Installateur" }, { value: "livreur", label: "Livreur" }]} />
        <TextField label="Nom complet" value={f.name} onChange={set("name")} required testid="prof-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Email" type="email" value={f.email} onChange={set("email")} required testid="prof-email" />
          <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="prof-phone" required={false} />
        </div>
        <TextField label="Mot de passe" value={f.password} onChange={set("password")} required testid="prof-password" />
        {f.role === "professional" && (
          <div>
            <span className="text-sm font-semibold text-slate-700">Compétences</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {SKILLS.map((s) => <button key={s} type="button" onClick={() => toggle("skills", s)} data-testid={`skill-${s}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${f.skills.includes(s) ? "bg-[#C85A32] text-white border-[#C85A32]" : "border-slate-300 text-slate-600"}`}>{s}</button>)}
            </div>
          </div>
        )}
        <div>
          <span className="text-sm font-semibold text-slate-700">Zones d'intervention</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {zones.map((z) => <button key={z.id} type="button" onClick={() => toggle("zones", z.name)} data-testid={`zone-${z.name}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${f.zones.includes(z.name) ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-600"}`}>{z.name}</button>)}
          </div>
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="prof-submit">Créer le compte</button>
      </form>
    </Modal>
  );
}

function EditProForm({ pro, zones, onClose, onDone }) {
  const [f, setF] = useState({ name: pro.name || "", phone: pro.phone || "", password: "", skills: pro.skills || [], zones: pro.zones || [], active: pro.active });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, val) => setF((s) => ({ ...s, [k]: s[k].includes(val) ? s[k].filter((x) => x !== val) : [...s[k], val] }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = { name: f.name, phone: f.phone, skills: f.skills, zones: f.zones, active: f.active };
    if (f.password) payload.password = f.password;
    try { await api.put(`/admin/professionals/${pro.user_id}`, payload); toast.success("Informations modifiées"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Modifier — ${pro.name}`} onClose={onClose} testid="pro-edit-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom complet" value={f.name} onChange={set("name")} required testid="edit-prof-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="edit-prof-phone" required={false} />
          <TextField label="Nouveau mot de passe" value={f.password} onChange={set("password")} testid="edit-prof-password" required={false} placeholder="Laisser vide pour ne pas changer" />
        </div>
        {pro.role === "professional" && (
          <div>
            <span className="text-sm font-semibold text-slate-700">Compétences</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {SKILLS.map((s) => <button key={s} type="button" onClick={() => toggle("skills", s)} data-testid={`edit-skill-${s}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${f.skills.includes(s) ? "bg-[#C85A32] text-white border-[#C85A32]" : "border-slate-300 text-slate-600"}`}>{s}</button>)}
            </div>
          </div>
        )}
        <div>
          <span className="text-sm font-semibold text-slate-700">Zones d'intervention</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {zones.map((z) => <button key={z.id} type="button" onClick={() => toggle("zones", z.name)} data-testid={`edit-zone-${z.name}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${f.zones.includes(z.name) ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-600"}`}>{z.name}</button>)}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active")(e.target.checked)} data-testid="edit-prof-active" /> Compte actif</label>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="edit-prof-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
